/**
 * User Busy Service — module-level signal for "user is doing something
 * we shouldn't interrupt".
 *
 * Purpose: when a user clicks to open a client chart (or any modal, or
 * any heavy load), background polls should STOP firing until the
 * interaction settles.  Otherwise the network competes with the user-
 * initiated request and the chart feels sluggish.
 *
 * Producers (things that call markBusy / markIdle):
 *   - Client chart loading
 *   - Note creation / edit dialog
 *   - Any modal/dialog of interest
 *
 * Consumers (things that check isUserBusy):
 *   - Background polling setIntervals (password resets, incomplete
 *     documentation, scheduled reviews, expiring releases, MDT
 *     pending actions, etc.)
 *
 * Reference-counted: many producers may mark busy for different
 * reasons; the service stays "busy" until EVERY reason has been
 * marked idle.  This avoids the "one component declares idle while
 * another is still working" race.
 *
 * Safety net: if a single reason stays busy >5 minutes, it auto-
 * resets to prevent stuck-busy bugs from permanently killing the
 * background polls.
 *
 * (Feb 2026 — Workstream B: "background requests need to be turned
 * off completely [while a chart loads], reducing that 50 requests
 * number down to just the 1 or 2 needed to get the patient's data.")
 */

// ---- Module-scope state — lives OUTSIDE React tree ------------------------
const MAX_BUSY_DURATION_MS = 5 * 60 * 1000; // 5 minutes per-reason safety net

/** Map<reason, { count: number, lastMarkBusyAt: number, safetyTimer: TimeoutID|null }> */
const _reasons = new Map();
const _subscribers = new Set();

// ---- Notify subscribers when overall busy state flips ---------------------
let _lastBusyState = false;
function _notifyIfChanged() {
  const isBusy = computeIsUserBusy();
  if (isBusy !== _lastBusyState) {
    _lastBusyState = isBusy;
    for (const sub of _subscribers) {
      try {
        sub(isBusy);
      } catch (e) {
        // Subscriber error never breaks the service.
        // eslint-disable-next-line no-console
        console.warn('[userBusyService] subscriber threw:', e);
      }
    }
  }
}

// ---- Per-reason safety net — auto-clear stuck-busy reasons ----------------
function _armSafetyTimer(reason) {
  const entry = _reasons.get(reason);
  if (!entry) return;
  if (entry.safetyTimer) clearTimeout(entry.safetyTimer);
  entry.safetyTimer = setTimeout(() => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `[userBusyService] reason "${reason}" stuck busy for >${MAX_BUSY_DURATION_MS / 1000}s — force-resetting`
      );
    }
    _reasons.delete(reason);
    _notifyIfChanged();
  }, MAX_BUSY_DURATION_MS);
}

// ---- Public API -----------------------------------------------------------

/**
 * Declare that the user is busy with ``reason``.  Reference-counted —
 * call markIdle(reason) the same number of times to clear it.
 *
 * @param {string} reason — short identifier (e.g. "chart-load",
 *   "modal:claim-edit", "appointment-form").
 */
export function markBusy(reason) {
  if (!reason) return;
  const entry = _reasons.get(reason) || { count: 0, lastMarkBusyAt: 0, safetyTimer: null };
  entry.count += 1;
  entry.lastMarkBusyAt = Date.now();
  _reasons.set(reason, entry);
  _armSafetyTimer(reason);
  _notifyIfChanged();
}

/**
 * Declare that the busy condition identified by ``reason`` is over.
 * Idempotent if called more times than markBusy — extras are no-ops
 * (better than negative counts which would imply a bug we want to
 * catch but not crash on).
 */
export function markIdle(reason) {
  if (!reason) return;
  const entry = _reasons.get(reason);
  if (!entry) return;
  entry.count -= 1;
  if (entry.count <= 0) {
    if (entry.safetyTimer) clearTimeout(entry.safetyTimer);
    _reasons.delete(reason);
    _notifyIfChanged();
  }
}

/**
 * Read the current busy state.  Safe to call from non-React code
 * (e.g. setInterval callbacks).
 */
export function isUserBusy() {
  return computeIsUserBusy();
}

function computeIsUserBusy() {
  for (const entry of _reasons.values()) {
    if (entry.count > 0) return true;
  }
  return false;
}

/**
 * Subscribe to busy-state transitions.  Returns an unsubscribe function.
 * Used by React components via the ``useIsUserBusy`` hook below.
 */
export function subscribeToBusyState(callback) {
  _subscribers.add(callback);
  return () => _subscribers.delete(callback);
}

/**
 * Read the list of active busy reasons.  Debug-only — used by the
 * dev-mode info panel and tests.
 */
export function getActiveBusyReasons() {
  return Array.from(_reasons.entries())
    .filter(([, entry]) => entry.count > 0)
    .map(([reason, entry]) => ({ reason, count: entry.count, lastMarkBusyAt: entry.lastMarkBusyAt }));
}

/**
 * Test-only: clear all busy reasons.  Not used by production code.
 */
export function _resetUserBusyServiceForTests() {
  for (const entry of _reasons.values()) {
    if (entry.safetyTimer) clearTimeout(entry.safetyTimer);
  }
  _reasons.clear();
  _lastBusyState = false;
}
